'use client'

import { Fragment, createElement, useEffect, type ReactNode } from 'react'
import posthog from 'posthog-js'
import type { Properties } from 'posthog-js'
import { isSubscriberPublicId } from '@mist/contracts/analytics'
import type { AnalyticsEventName, AnalyticsProperties } from '@mist/contracts/analytics'
import { drainBufferedAnalytics, setAnalyticsSink, type AnalyticsRecord } from './analytics'
import { getAnonymousId, getSessionId } from './attribution'
import { readSubscriberHandoff } from './session'

export const POSTHOG_EXCLUDED_ROUTE_PREFIXES = ['/verify', '/staff'] as const
export const SAFE_POSTHOG_FEATURE_FLAGS = ['mist_cta_copy', 'mist_static_vs_3d'] as const

const ALIAS_STORAGE_KEY = 'mist.posthog.alias.subscriber'

export interface PostHogPublicEnv {
  readonly NEXT_PUBLIC_POSTHOG_KEY?: string | undefined
  readonly NEXT_PUBLIC_POSTHOG_HOST?: string | undefined
}

export interface MistPostHogClientOptions {
  readonly api_host: string
  readonly defaults: '2026-05-30'
  readonly autocapture: false
  readonly capture_pageview: false
  readonly disable_session_recording: false
  readonly mask_all_text: true
  readonly mask_all_element_attributes: true
  readonly session_recording: {
    readonly maskAllInputs: true
    readonly maskTextSelector: '*'
    readonly blockSelector: string
  }
  readonly capture_performance: {
    readonly web_vitals: true
    readonly web_vitals_allowed_metrics: ['CLS', 'INP', 'LCP', 'FCP', 'TTFB']
  }
}

export interface SubscriberAliasInput {
  readonly anonymousId: string
  readonly sessionId: string
  readonly subscriberPublicId: string
}

export interface SubscriberAliasPlan {
  readonly distinctId: string
  readonly aliases: readonly string[]
}

const DEFAULT_PUBLIC_ENV: PostHogPublicEnv = {
  NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
}

let capabilityTier: 'static' | 'enhanced' | undefined

let initialized = false

export function shouldInitializePostHog(pathname: string): boolean {
  const cleanPath = pathname.split(/[?#]/, 1)[0] || '/'
  return !POSTHOG_EXCLUDED_ROUTE_PREFIXES.some((prefix) => cleanPath === prefix || cleanPath.startsWith(`${prefix}/`))
}

export function buildPostHogClientOptions(env: PostHogPublicEnv = DEFAULT_PUBLIC_ENV, pathname = '/'): MistPostHogClientOptions {
  if (!shouldInitializePostHog(pathname)) {
    throw new Error('POSTHOG_ROUTE_EXCLUDED')
  }
  const host = env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com'
  return {
    api_host: host,
    defaults: '2026-05-30',
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: '[data-posthog-block], [data-sensitive], input, textarea, select, [contenteditable="true"]',
    },
    capture_performance: {
      web_vitals: true,
      web_vitals_allowed_metrics: ['CLS', 'INP', 'LCP', 'FCP', 'TTFB'],
    },
  }
}

export function buildSubscriberAliasPlan(input: SubscriberAliasInput): SubscriberAliasPlan {
  if (!isSubscriberPublicId(input.subscriberPublicId)) throw new Error('SUBSCRIBER_ALIAS_MUST_BE_PUBLIC_UUID')
  const aliases: string[] = []
  if (input.anonymousId && input.anonymousId !== input.subscriberPublicId) aliases.push(input.anonymousId)
  if (input.sessionId && input.sessionId !== input.subscriberPublicId && input.sessionId !== input.anonymousId) aliases.push(input.sessionId)
  return { distinctId: input.subscriberPublicId, aliases }
}

export function initializeMistPostHog(
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
  env: PostHogPublicEnv = DEFAULT_PUBLIC_ENV,
): boolean {
  if (typeof window === 'undefined') return false
  if (!shouldInitializePostHog(pathname)) {
    setAnalyticsSink(undefined)
    if (initialized) posthog.stopSessionRecording()
    return false
  }

  const sink = (record: AnalyticsRecord) => capturePostHogEvent(record)
  if (initialized) {
    setAnalyticsSink(sink)
    drainBufferedAnalytics(sink)
    if (capabilityTier) posthog.register({ capability_tier: capabilityTier })
    aliasVerifiedSubscriber()
    posthog.startSessionRecording()
    return true
  }

  const projectKey = env.NEXT_PUBLIC_POSTHOG_KEY
  if (!projectKey) return false

  posthog.init(projectKey, {
    ...buildPostHogClientOptions(env, pathname),
    bootstrap: { distinctID: getAnonymousId() },
  } as Parameters<typeof posthog.init>[1])
  initialized = true
  setAnalyticsSink(sink)
  drainBufferedAnalytics(sink)
  if (capabilityTier) posthog.register({ capability_tier: capabilityTier })
  aliasVerifiedSubscriber()
  return true
}

export function registerPostHogCapabilityTier(tier: 'static' | 'enhanced'): void {
  capabilityTier = tier
  if (initialized) posthog.register({ capability_tier: tier })
}

export function PostHogClientProvider({
  children,
  env,
}: {
  readonly children: ReactNode
  readonly env?: PostHogPublicEnv
}) {
  useEffect(() => {
    initializeMistPostHog(window.location.pathname, env)
    return () => {
      setAnalyticsSink(undefined)
      if (initialized) posthog.stopSessionRecording()
    }
  }, [env])
  return createElement(Fragment, null, children)
}

function capturePostHogEvent(record: AnalyticsRecord): void {
  posthog.capture(record.name as AnalyticsEventName, record.properties as AnalyticsProperties & Properties)
}

function aliasVerifiedSubscriber(): void {
  const handoff = readSubscriberHandoff()
  if (!handoff) return
  const plan = buildSubscriberAliasPlan({ anonymousId: getAnonymousId(), sessionId: getSessionId(), subscriberPublicId: handoff.subscriberPublicId })
  if (window.localStorage.getItem(ALIAS_STORAGE_KEY) === plan.distinctId) return
  for (const alias of plan.aliases) posthog.alias(plan.distinctId, alias)
  posthog.identify(plan.distinctId)
  window.localStorage.setItem(ALIAS_STORAGE_KEY, plan.distinctId)
}
